// ===========================================
// Enrollment Trend Chart Component
// ===========================================
// Multi-line chart showing enrollment trends over time
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React from 'react';
import { Paper, Typography, Box, Skeleton } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { EnrollmentTrendRecord } from '../../api/reports.api';

interface EnrollmentTrendChartProps {
  data?: EnrollmentTrendRecord[];
  isLoading: boolean;
}

const EnrollmentTrendChart: React.FC<EnrollmentTrendChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Enrollment Trends
        </Typography>
        <Skeleton variant="rectangular" height={400} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Enrollment Trends
        </Typography>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">
            No enrollment trend data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  const chartData = data.map((trend) => ({
    month: trend.monthLabel,
    active: trend.activeEnrollments,
    new: trend.newEnrollments,
    withdrawn: trend.withdrawals,
    netGrowth: trend.netGrowth,
  }));

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Enrollment Trends
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track active enrollments, new enrollments, and withdrawals over time
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis label={{ value: 'Student Count', angle: -90, position: 'insideLeft' }} />
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length > 0) {
                const data = payload[0].payload;
                return (
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2">{data.month}</Typography>
                    <Typography variant="body2" sx={{ color: '#4580E4' }}>
                      Active: {data.active}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5cb399' }}>
                      New: {data.new}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#FFAE9E' }}>
                      Withdrawn: {data.withdrawn}
                    </Typography>
                    <Typography variant="caption" display="block" fontWeight="bold">
                      Net Growth: {data.netGrowth > 0 ? '+' : ''}
                      {data.netGrowth}
                    </Typography>
                  </Box>
                );
              }
              return null;
            }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="active"
            name="Active Enrolments"
            stroke="#4580E4"
            strokeWidth={3}
            dot={{ r: 5 }}
          />
          <Line
            type="monotone"
            dataKey="new"
            name="New Enrolments"
            stroke="#5cb399"
            strokeWidth={2}
            dot={{ r: 4 }}
          />
          <Line
            type="monotone"
            dataKey="withdrawn"
            name="Withdrawals"
            stroke="#FFAE9E"
            strokeWidth={2}
            dot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default EnrollmentTrendChart;
